<template>
  <div class="rm-drawer-step">
    <div class="rm-drawer-scroll">
      <h3 class="drawer-section-title mb-4">
        {{ $t('ESTIMATE_REPAIRER_OFFER.SUB_TITLE') }}
      </h3>

      <div class="repairer-offer-list mb-4">
        <template
          v-for="repairer_estimate_option of currentRepairFile?.repairer_estimate
            ?.repairer_estimate_option_list"
          :key="repairer_estimate_option.technical_id"
        >
          <v-card-secondary class="repairer-offer-card">
            <div class="drawer-form-header repairer-offer-card-header">
              <v-chip class="repairer-offer-chip" color="primary" size="small" variant="tonal">
                {{
                  $t(
                    'COMMON.PRODUCT_END_STATUS.' + repairer_estimate_option.offer_type.toUpperCase()
                  )
                }}
              </v-chip>
              <div class="repairer-offer-actions">
                <v-btn-icon
                  v-if="
                    !visibleMarginInputField.includes(repairer_estimate_option.technical_id) &&
                    !visiblePriceInputField.includes(repairer_estimate_option.technical_id)
                  "
                  :title="$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_PRICE')"
                  icon="mdi-pencil-outline"
                  size="small"
                  @click="togglePriceTextField(repairer_estimate_option.technical_id)"
                />
                <v-btn-icon
                  v-if="
                    !visibleMarginInputField.includes(repairer_estimate_option.technical_id) &&
                    !visiblePriceInputField.includes(repairer_estimate_option.technical_id)
                  "
                  :title="$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_MARGIN')"
                  icon="mdi-percent-outline"
                  size="small"
                  @click="toggleMarginTextField(repairer_estimate_option.technical_id)"
                />
                <v-btn-icon
                  v-if="
                    !visibleMarginInputField.includes(repairer_estimate_option.technical_id) &&
                    !visiblePriceInputField.includes(repairer_estimate_option.technical_id)
                  "
                  color="error"
                  icon="mdi-trash-can-outline"
                  size="small"
                  @click="deleteRepairerEstimateOption(repairer_estimate_option)"
                />
              </div>
            </div>

            <div class="detail-info-row repairer-offer-comment-row">
              <span class="detail-info-label">{{
                $t('ESTIMATE_REPAIRER_OFFER.DETAIL_CLIENT')
              }}</span>
              <strong class="detail-info-value">
                {{ $displayValue(repairer_estimate_option.comment) }}
              </strong>
            </div>

            <div class="repairer-offer-metrics">
              <div class="secondary-card--soft repairer-choice-metric">
                <p class="repairer-choice-metric-label">
                  {{ $t('ESTIMATE_REPAIRER_OFFER.CLIENT_PRICE') }}
                </p>
                <p class="repairer-choice-metric-value">
                  {{ Number(repairer_estimate_option.total_cost).toFixed(2) }}
                  {{ $t('COMMON.EURO') }}
                </p>
              </div>

              <div class="secondary-card--soft repairer-choice-metric">
                <p class="repairer-choice-metric-label">
                  {{
                    $t(
                      isReplacementEstimateOffer(repairer_estimate_option)
                        ? 'COMMON.COST'
                        : 'ESTIMATE_REPAIRER_OFFER.REPAIRER_COST_SHORT'
                    )
                  }}
                </p>
                <p class="repairer-choice-metric-value">
                  {{
                    (
                      repairer_estimate_option.estimate_cost +
                      repairer_estimate_option.transport_cost +
                      repairer_estimate_option.labor_cost
                    ).toFixed(2)
                  }}
                  {{ $t('COMMON.EURO') }}
                </p>
              </div>

              <div class="secondary-card--soft repairer-choice-metric">
                <p class="repairer-choice-metric-label">
                  {{ $t('ESTIMATE_REPAIRER_OFFER.MARGIN') }}
                </p>
                <p class="repairer-choice-metric-value">
                  {{ repairer_estimate_option.margin }} {{ $t('COMMON.PERCENT') }}
                </p>
              </div>
            </div>
          </v-card-secondary>
          <v-form-card
            v-if="
              hasVisibleEditForm &&
              activeEditOption.technical_id === repairer_estimate_option.technical_id
            "
            ref="formUpdateEstimateRepairerOption"
            v-model="isUpdateOptionFormValid"
            @submit.prevent="submitEditForm"
          >
            <div class="drawer-form-header">
              <p class="drawer-form-title">{{ activeEditTitle }}</p>
              <v-btn-icon
                class="drawer-form-close"
                icon="mdi-close"
                size="small"
                @click="closeEditForm"
              />
            </div>

            <div class="drawer-form-field">
              <p class="custom-label" required="true">{{ activeEditLabel }}</p>
              <v-text-field
                v-if="activeEditType === 'totalCost'"
                v-model="activeEditOption.total_cost"
                type="number"
                :rules="numberRules"
                hide-details="auto"
              >
                <template #append-inner>
                  <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
                </template>
              </v-text-field>
              <v-text-field
                v-if="activeEditType === 'margin'"
                v-model="activeEditOption.margin"
                type="number"
                :rules="marginRules"
                hide-details="auto"
              >
                <template #append-inner>
                  <span class="drawer-field-suffix">{{ $t('COMMON.PERCENT') }}</span>
                </template>
              </v-text-field>
            </div>

            <v-btn-secondary
              class="w-100"
              :disabled="!isUpdateOptionFormValid || loading"
              :loading="loading"
              prepend-icon="mdi-check"
              variant="tonal"
              type="submit"
            >
              {{ activeEditSaveLabel }}
            </v-btn-secondary>
          </v-form-card>
        </template>
      </div>

      <button
        v-if="
          checkRepairFileEndStatusListNotNull() && !showRepairerOptionForm && !hasVisibleEditForm
        "
        class="repairer-offer-add-card"
        type="button"
        @click="showRepairerOptionForm = true"
      >
        <span class="repairer-offer-add-icon">
          <v-icon icon="mdi-plus" size="18" />
        </span>
        <span>{{ $t('ESTIMATE_REPAIRER_OFFER.BUTTON.ADD_FORM') }}</span>
      </button>

      <!--FORM OPTION-->
      <v-form-card
        v-if="showRepairerOptionForm && !hasVisibleEditForm"
        ref="formEstimateRepairerOption"
        v-model="isOptionFormValid"
        @submit.prevent="addEstimateRepairerOption"
      >
        <div class="drawer-form-header">
          <p class="drawer-form-title">{{ $t('ESTIMATE_REPAIRER_OFFER.NEW_OFFER') }}</p>
          <v-btn-icon
            class="drawer-form-close"
            icon="mdi-close"
            size="small"
            @click="showRepairerOptionForm = false"
          />
        </div>

        <div class="drawer-form-field">
          <p class="custom-label" required="true">
            {{ $t('ESTIMATE_REPAIRER_OFFER.OFFER_TYPE') }}
          </p>
          <custom-autocomplete
            id="select"
            v-model="repairFileEndStatusSelected"
            :items="repairFileEndStatusList"
            :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.OFFER_TYPE')"
            item-title="name"
            item-value="technical_id"
            required
            :rules="requiredRules"
            hide-details="auto"
          />
        </div>

        <div class="drawer-form-field">
          <p class="custom-label" required="true">
            {{ $t('ESTIMATE_REPAIRER_OFFER.DETAIL_CLIENT') }}
          </p>
          <v-textarea
            v-model.trim="comment"
            :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.COMMENT')"
            :rules="requiredRules"
            auto-grow
            rows="2"
            :hide-details="!commentIsFocused"
            :maxlength="500"
            :counter="commentIsFocused"
            @update:focused="commentIsFocused = $event"
          />
        </div>

        <div class="repairer-offer-form-grid">
          <div class="drawer-form-field">
            <p class="custom-label" required="true">
              {{
                $t(
                  isReplacementEstimateOffer({ offer_type: repairFileEndStatusSelected })
                    ? 'COMMON.COST_EXCLUSIVE_TAX'
                    : 'ESTIMATE_REPAIRER_OFFER.REPAIR_COST_HT'
                )
              }}
            </p>
            <v-text-field
              v-model="estimateCost"
              :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.ESTIMATE_COST')"
              type="number"
              :rules="[...requiredRules, ...numberRules]"
              hide-details="auto"
            >
              <template #append-inner>
                <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
              </template>
            </v-text-field>
          </div>

          <div class="drawer-form-field">
            <p class="custom-label">{{ $t('ESTIMATE_REPAIRER_OFFER.TRANSPORT_HT') }}</p>
            <v-text-field
              v-model="transportCost"
              :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.TRANSPORT_COST')"
              type="number"
              :rules="numberRules"
              hide-details="auto"
            >
              <template #append-inner>
                <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
              </template>
            </v-text-field>
          </div>

          <div v-if="repairFileEndStatusSelected === 'PRODUCT_REPAIRED'" class="drawer-form-field">
            <p class="custom-label">{{ $t('ESTIMATE_REPAIRER_OFFER.LABOR_HT') }}</p>
            <v-text-field
              v-model="laborCost"
              :placeholder="$t('ESTIMATE_REPAIRER_OFFER.PLACEHOLDER.LABOR_COST')"
              type="number"
              :rules="numberRules"
              hide-details="auto"
            >
              <template #append-inner>
                <span class="drawer-field-suffix">{{ $t('COMMON.EURO') }}</span>
              </template>
            </v-text-field>
          </div>
        </div>

        <v-btn-secondary
          class="w-100"
          :disabled="!isOptionFormValid || loading || repairFileEndStatusSelected === null"
          :loading="loading"
          prepend-icon="mdi-plus"
          variant="tonal"
          type="submit"
        >
          {{ $t('ESTIMATE_REPAIRER_OFFER.BUTTON.VALIDATE_OFFER') }}
        </v-btn-secondary>
      </v-form-card>

      <p class="repairer-offer-info">
        {{ $t('ESTIMATE_REPAIRER_OFFER.INFO_CLIENT') }}
      </p>
    </div>
  </div>
</template>

<script>
import RepairFileEndStatusConstant from '@/constants/repair-module/repair-file/repairFileEndStatusConstant'
import EstimateRepairerService from '@/services/EstimateRepairerService'
import Validators from '@/utils/validators'
import { isReplacementEstimateOffer } from '@/utils/repairFileUtils'
import { handleResponseError } from '@/utils/toastUtils'

export default {
  props: {
    currentRepairFile: Object
  },
  data() {
    return {
      loading: false,
      error: null,
      isOptionFormValid: false,
      isUpdateOptionFormValid: false,
      showRepairerOptionForm: false,
      repairFileEndStatusList: [],
      repairFileEndStatusSelected: null,
      comment: null,
      commentIsFocused: false,
      estimateCost: null,
      transportCost: null,
      laborCost: null,
      totalCost: null,
      baseMargin: 25,
      visiblePriceInputField: [],
      visibleMarginInputField: [],

      requiredRules: Validators.requiredRules,
      numberRules: Validators.numberRules,
      marginRules: Validators.marginRules
    }
  },
  created() {
    //Remove already selected status
    this.patchRepairFileEndStatusList()
  },
  mounted() {
    this.emitFooterState()
  },
  computed: {
    activePriceEditOption() {
      const activeId = this.visiblePriceInputField[0]
      return this.findRepairerEstimateOption(activeId)
    },
    activeMarginEditOption() {
      const activeId = this.visibleMarginInputField[0]
      return this.findRepairerEstimateOption(activeId)
    },
    activeEditOption() {
      return this.activePriceEditOption || this.activeMarginEditOption
    },
    activeEditType() {
      if (this.activePriceEditOption) return 'totalCost'
      if (this.activeMarginEditOption) return 'margin'
      return null
    },
    activeEditTitle() {
      if (this.activeEditType === 'totalCost') {
        return this.$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_PRICE')
      }
      if (this.activeEditType === 'margin') {
        return this.$t('ESTIMATE_REPAIRER_OFFER.MODIFICATION_MARGIN')
      }
      return null
    },
    activeEditLabel() {
      if (this.activeEditType === 'totalCost') {
        return this.$t('ESTIMATE_REPAIRER_OFFER.CLIENT_PRICE_HT')
      }
      if (this.activeEditType === 'margin') return this.$t('ESTIMATE_REPAIRER_OFFER.MARGIN')
      return null
    },
    activeEditSaveLabel() {
      if (this.activeEditType === 'totalCost') {
        return this.$t('ESTIMATE_REPAIRER_OFFER.SAVE_PRICE')
      }
      if (this.activeEditType === 'margin') {
        return this.$t('ESTIMATE_REPAIRER_OFFER.SAVE_MARGIN')
      }
      return this.$t('COMMON.BUTTON.SAVE')
    },
    hasVisibleEditForm() {
      return Boolean(this.activeEditOption && this.activeEditType)
    },
    footerState() {
      return {
        disabled:
          this.loading ||
          this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list.length === 0,
        loading: this.loading
      }
    }
  },
  watch: {
    footerState: {
      handler() {
        this.emitFooterState()
      },
      deep: true
    },
    currentRepairFile() {
      this.patchRepairFileEndStatusList()
    },
    showRepairerOptionForm() {
      this.patchRepairFileEndStatusList()
    }
  },
  methods: {
    isReplacementEstimateOffer,
    emitFooterState() {
      this.$emit('updateFooterState', this.footerState)
    },
    findRepairerEstimateOption(id) {
      if (!id) return null
      return this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list.find(
        (option) => option.technical_id === id
      )
    },
    toggleMarginTextField(id) {
      if (this.visibleMarginInputField.includes(id)) {
        this.visibleMarginInputField = []
      } else {
        this.visiblePriceInputField = []
        this.visibleMarginInputField = [id]
        this.showRepairerOptionForm = false
      }
      this.isUpdateOptionFormValid = false
    },
    togglePriceTextField(id) {
      if (this.visiblePriceInputField.includes(id)) {
        this.visiblePriceInputField = []
      } else {
        this.visibleMarginInputField = []
        this.visiblePriceInputField = [id]
        this.showRepairerOptionForm = false
      }
      this.isUpdateOptionFormValid = false
    },
    closeEditForm() {
      this.visiblePriceInputField = []
      this.visibleMarginInputField = []
      this.isUpdateOptionFormValid = false
    },
    submitEditForm() {
      if (!this.activeEditOption || !this.activeEditType) return
      this.updateRepairerEstimateOption(this.activeEditOption, this.activeEditType)
    },
    checkRepairFileEndStatusListNotNull() {
      return this.repairFileEndStatusList.length !== 0
    },
    patchRepairFileEndStatusList() {
      // Create a list of repair file end statuses
      this.repairFileEndStatusList = Object.keys(RepairFileEndStatusConstant).map((key) => ({
        technical_id: key,
        name: this.$t(
          `REPAIR_FILE_DELIVERY_CLIENT_DRAWER.PRODUCT_END_STATUS.${RepairFileEndStatusConstant[key]}`
        )
      }))
      // Filter the list to exclude statuses already present in repairer estimate options
      this.repairFileEndStatusList = this.repairFileEndStatusList.filter((status) => {
        return !this.currentRepairFile?.repairer_estimate?.repairer_estimate_option_list.some(
          (option) => option.offer_type.toUpperCase() === status.technical_id
        )
      })
    },
    async addEstimateRepairerOption() {
      this.loading = true

      this.calculateBaseTotalCost()

      try {
        const data = {
          repair_file_id: this.currentRepairFile.technical_id,
          repairer_estimate_id: this.currentRepairFile.repairer_estimate.technical_id,
          offer_type: this.repairFileEndStatusSelected,
          comment: this.comment,
          margin: this.baseMargin,
          estimate_cost: this.estimateCost,
          transport_cost: this.transportCost || 0,
          labor_cost: this.laborCost || 0,
          total_cost: this.totalCost
        }
        await EstimateRepairerService.createRepairFileRepairerEstimateOption(data)
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        this.showRepairerOptionForm = false
        this.repairFileEndStatusSelected = null
        this.comment = null
        this.estimateCost = null
        this.transportCost = null
        this.laborCost = null
        this.totalCost = null
        this.error = null
        this.patchRepairFileEndStatusList()
      }
    },
    async updateRepairerEstimateOption(repairerEstimateOption, type) {
      this.loading = true

      if (type === 'totalCost') {
        this.calculateNewMargin(repairerEstimateOption)
      } else if (type === 'margin') {
        this.calculateNewTotalCost(repairerEstimateOption)
      }

      try {
        const data = {
          repair_file_id: this.currentRepairFile.technical_id,
          repairer_estimate_id: this.currentRepairFile.repairer_estimate.technical_id,
          repairer_estimate_option_id: repairerEstimateOption.technical_id,
          offer_type: repairerEstimateOption.offer_type,
          comment: repairerEstimateOption.comment,
          margin: Number(repairerEstimateOption.margin),
          estimate_cost: repairerEstimateOption.estimate_cost,
          transport_cost: repairerEstimateOption.transport_cost || 0,
          labor_cost: repairerEstimateOption.labor_cost || 0,
          total_cost: Number(repairerEstimateOption.total_cost)
        }
        await EstimateRepairerService.updateRepairFileRepairerEstimateOption(data)
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async deleteRepairerEstimateOption(repairerEstimateOption) {
      this.loading = true
      try {
        const data = {
          repair_file_id: this.currentRepairFile.technical_id,
          repairer_estimate_option_id: repairerEstimateOption.technical_id
        }
        await EstimateRepairerService.deleteRepairFileRepairerEstimateOption(data)
        this.$emit('updateCurrentRepairFile')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    async submit() {
      this.loading = true
      try {
        const data = {
          repair_file_id: this.currentRepairFile.technical_id
        }
        await EstimateRepairerService.confirmationRepairFileRepairerEstimate(data)
        this.$emit('updateCurrentRepairFile')
        this.$emit('openEstimateRepairerDetail')
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        this.patchRepairFileEndStatusList()
      }
    },
    calculateBaseTotalCost() {
      this.totalCost =
        (Number(this.estimateCost) + Number(this.transportCost) + Number(this.laborCost)) /
        (1 - this.baseMargin / 100)
    },
    calculateNewMargin(repairerEstimateOption) {
      const newMargin =
        (100 *
          (repairerEstimateOption.total_cost -
            (repairerEstimateOption.estimate_cost +
              repairerEstimateOption.transport_cost +
              repairerEstimateOption.labor_cost))) /
        repairerEstimateOption.total_cost
      repairerEstimateOption.margin = Number(newMargin.toFixed(0))
      this.togglePriceTextField(repairerEstimateOption.technical_id)
    },
    calculateNewTotalCost(repairerEstimateOption) {
      const newTotalCost =
        (repairerEstimateOption.estimate_cost +
          repairerEstimateOption.transport_cost +
          repairerEstimateOption.labor_cost) /
        (1 - repairerEstimateOption.margin / 100)
      repairerEstimateOption.total_cost = parseFloat(newTotalCost)
      this.toggleMarginTextField(repairerEstimateOption.technical_id)
    }
  }
}
</script>

<style scoped>
.repairer-offer-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.repairer-offer-card {
  padding: 16px;
}

.repairer-offer-card-header {
  justify-content: space-between;
}

.repairer-offer-chip {
  max-width: calc(100% - 112px);
}

.repairer-offer-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.repairer-offer-comment-row {
  margin: 8px 0 16px;
}

.repairer-offer-metrics,
.repairer-offer-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.repairer-offer-metrics {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.repairer-offer-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  min-height: 96px;
  padding: 16px;
  border: 2px dashed #d8dee4;
  border-radius: 12px;
  background-color: var(--soft-surface-color);
  color: rgb(var(--v-theme-on-surface));
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
}

.repairer-offer-add-card:hover {
  border-color: rgba(var(--v-theme-primary), 0.55);
  background: rgba(var(--v-theme-primary), 0.04);
}

.repairer-offer-add-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

.repairer-offer-info {
  margin: 12px 0 0;
  padding-inline: 14px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 13px;
  line-height: 1.4;
}

@media (max-width: 420px) {
  .repairer-offer-metrics,
  .repairer-offer-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
